import React, { useEffect, useState, useLayoutEffect, useRef } from "react";

import CovertButton from "@cayuse/shared-react.components.covert-button";
import Icon from "@cayuse/shared-react.components.icon";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";
import { isNonEmptyArray, isNonEmptyObject, isNonEmptyString, isNumber } from "@cayuse/shared-react.utils.type-checks";
import { isEqual } from "lodash";

import { RESEARCH_TEAM } from "globals/constants/formObjects";
import { useDictionary, useMediaQuery } from "hooks";

import FlowableFinder from "../Common/FlowableFinder";
import FlowableOrgSearch from "../FlowableOrgSearch/FlowableOrgSearch";
import { initialAssociation } from "./constants";
import useEffortCalculation from "./custom-hooks/useEffortCalculation";
import styles from "./styles.css";
import {
	sanitizeNumberValue,
	validateEffort,
	validateIsRequired,
	getNumberConfig,
	getSelectConfig,
	getCellWidth,
	getEffortConfigs,
	deepCopy
} from "./utils";

const { EFFORT_FORMAT, ASSOCIATION_TYPE } = RESEARCH_TEAM;

const EFFORT_TYPES = [ "sponsored", "costShare" ];

const mapAssociations = (association, key) => {
	return {
		key: key,
		value: association.id,
		text: association.text,
		association: association,
		disabled: association.disabled,
		content: (
			<div className="optionContent">
				<span>{association.text}</span>
			</div>
		),
	};
};

const getValidateRequiredClass = (value, enabled, isFieldRequired) => {
	if (!isNumber(value) && enabled && isFieldRequired) {
		return `${styles.emptyRequired} required`;
	} else {
		return "";
	}
};

export const RTAssociationRow = props => {
	const {
		memberAssociations,
		config,
		onChange,
		index,
		subIndex,
		record,
		rowKey,
		Components,
		teamMembers,
		updateValue,
		restProps,
		effortFormat,
		displayConditions,
		associationType,
		guestMode,
		removeAssociation,
		loukups,
	} = props;

	const dictionary = useDictionary("researchTeam");
	const ref = useRef(null);

	const [ mounted, setMounted ] = useState();
	const [ preselectedOptions, setPreselectedOptions ] = useState([]);
	const [ showModal, setShowModal ] = useState(false);
	const [ association, setAssociation ] = useState(deepCopy(initialAssociation));
	const [ associationLookup, setAssociationLookup ] = useState({});
	const [ associationConfig, setAssociationsConfig ] = useState();
	const [ costShareEffortConfigs, setCostShareEffortConfigs ] = useState();
	const [ sponsoredConfigs, setSponsoredConfigs ] = useState();
	const [ creditConfig, setCreditConfig ] = useState();

	const fieldFormat = effortFormat === EFFORT_FORMAT.MONTHS ? "Months" : "";
	const isSmallDesktopView = useMediaQuery("(min-width: 992px) and (max-width: 1200px)");
	const isTabletView = useMediaQuery("(max-width: 992px)");
	const isNotDesktopView = useMediaQuery("(max-width: 1200px)");

	useLayoutEffect(() => {
		if (!ref.current) {
			return;
		}

		const isElementParentContainClass = (elem, label) => {
			return elem && elem.parentElement.classList.contains(label);
		};

		const inputs = ref.current.querySelectorAll("input");

		inputs.forEach(input => {
			Object.keys(dictionary.inputLabels).forEach(label => {
				if (isElementParentContainClass(input, label)) {
					input.setAttribute("aria-label", dictionary.inputLabels[`${label}`]);
				} else if (isElementParentContainClass(input, "required")) {
					input.setAttribute("required", true);
				}
			});
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ associationType, association.credit ]);

	const updateAssociation = newWalue => {
		if (associationType === ASSOCIATION_TYPE.EXTERNAL) {
			updateValue(onChange, (value => {
				value[index].externalAssociations[subIndex] = {
					association: newWalue,
				};
			}));
		} else {
			updateValue(onChange, (value => {
				value[index].associations[subIndex] = {
					...deepCopy(initialAssociation),
					association: newWalue,
				};
			}));
		}
	};

	// if association exists and lookup does not
	const associationHasBeenRemoved = associationType === ASSOCIATION_TYPE.INTERNAL &&
		association.association &&
		displayConditions.shouldDisplayAtLeastOneEffortField &&
		(!isNonEmptyObject(associationLookup) || !!associationLookup.removed);

	useEffect(() => {
		setAssociation(deepCopy(record));
		setMounted(true);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ ]);

	useEffect(() => {
		const associationSelectConfig = getSelectConfig(
			associationType === ASSOCIATION_TYPE.INTERNAL ? "Internal Association" : "External Association",
			config.$path + "associations.0",
			index,
			"association",
			memberAssociations,
			{
				isRequired: config.isRequired,
				enabled: config.enabled
			}
		);
		setAssociationsConfig(associationSelectConfig);

		const creditInputConfig = getNumberConfig("", config.$path + ".0", index, "credit", record, config.enabled, displayConditions.creditFieldRequired);
		setCreditConfig(creditInputConfig);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ config.enabled ]);

	useEffect(() => {
		if(association.association) {
			const lookup = loukups.find(item => item.id === association.association) ?? {};
			setAssociationLookup(lookup);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ association.association ]);

	useEffect(() => {
		const isEnabled = config.enabled && !associationHasBeenRemoved;
		const configs = getEffortConfigs({
			index,
			path: config.$path,
			fieldFormat,
			effortFormat,
			lookup: associationLookup,
			isEnabled
		});
		setCostShareEffortConfigs(configs.costShare);
		setSponsoredConfigs(configs.sponsoredNumber);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ associationLookup, effortFormat, config.enabled ]);

	useEffect(() => {
		const findDuplicateIds = associations => {
			const countMap = associations.reduce((map, { id }) => {
				map[id] = (map[id] || 0) + 1;

				return map;
			}, {});

			return new Set(Object.keys(countMap).filter(id => countMap[id] > 1));
		};

		const duplicateIds = findDuplicateIds(memberAssociations ?? []);
		const options = (memberAssociations ?? []).map((association, index) => {
			const key = duplicateIds.has(association.id) ? `${association.id}-${index}` : association.id;

			return mapAssociations(association, key);
		});

		setPreselectedOptions(options);
	}, [ memberAssociations ]);

	useEffect(() => {
		if (associationHasBeenRemoved && record.details?.name) {
			if (isNonEmptyString(record.details?.name)) {
				// use timeout because of async renderings
				setTimeout(() => {
					// set unit name because removed unit shows id
					const selectValueElement = ref.current.querySelector(".flw__select__single-value");
					if(selectValueElement) {
						selectValueElement.innerHTML = record?.details?.name;
					}
				});
			}
		}
	}, [ record, associationHasBeenRemoved, ref ]);

	useEffect(() => {
		// calculate and save effort fields on the very first load for existing records
		if (association && associationType === ASSOCIATION_TYPE.INTERNAL && association.association) {
			if (teamMembers[index].isInitialCalculationRequired) {
				setInitialCalculatedEffortValues();
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ association ]);

	const setEffortValue = (effortType, type, newValue) => {
		const field = type === EFFORT_TYPES[0] ? `sponsoredEffort${fieldFormat}` : `costShareEffort${fieldFormat}`;
		setAssociation(prevState =>
			({ ...prevState,
				[field]:
					{ ...prevState[field], [effortType]: newValue } }));

		const values = convertMonthsAndPercentage({
			...association,
			[field]:
				{ ...association[field],
					[effortType]: newValue }
		}, effortFormat);
		const total = calculateTotalEffort(values);
		setAssociation(prev => ({
			...prev,
			...values,
			totalEffort: total.totalEffort,
			totalEffortMonths: total.totalEffortMonths,
		}));
		updateValue(onChange, (value => {
			if(type === EFFORT_TYPES[0]) {
				value[index].associations[subIndex].sponsoredEffort[effortType] = values.sponsoredEffort[effortType];
				value[index].associations[subIndex].sponsoredEffortMonths[effortType] = values.sponsoredEffortMonths[effortType];
			} else {
				value[index].associations[subIndex].costShareEffort[effortType] = values.costShareEffort[effortType];
				value[index].associations[subIndex].costShareEffortMonths[effortType] = values.costShareEffortMonths[effortType];
			}
			value[index].associations[subIndex].totalEffort = total.totalEffort;
			value[index].associations[subIndex].totalEffortMonths = total.totalEffortMonths;
		}));
	};

	const [ setInitialCalculatedEffortValues, calculateTotalEffort, convertMonthsAndPercentage ] = useEffortCalculation(
		updateValue,
		onChange,
		index,
		subIndex,
		association,
		{
			calendar: associationLookup.calendarMonths,
			academic: associationLookup.academicMonths,
			summer: associationLookup.summerMonths,
		}
	);

	const trashIconEnabled = config.enabled;

	const associationChanged = associations => {
		if (teamMembers.length > 0) {
			const mappedAssociations = (associations || []).map(association =>
				association.key ? association : { key: association, value: association }
			);
			const newValue = isNonEmptyArray(mappedAssociations) ? mappedAssociations[0].value : null;

			setAssociation({ ...deepCopy(initialAssociation), association: newValue });
			updateAssociation(newValue);
		}
	};

	const creditChanged = newValue => {
		setAssociation(prev => ({
			...prev,
			credit: sanitizeNumberValue(newValue)
		}));
		updateValue(onChange, (value => {
			value[index].associations[subIndex].credit = newValue;
		}));
	};

	const remove = () => {
		removeAssociation(subIndex);
		setShowModal(false);
	};

	const asterisk = <span className={styles.red}> *</span>;

	if(!mounted) {
		return null;
	}

	return (
		<div
			key={`row-${index}-${subIndex}`}
			className={styles.associationsRowWrapper}
			ref={ref}
		>
			<div
				className={styles.cellWrapper}
				style={{ width: getCellWidth("internalAssociation", isSmallDesktopView, isTabletView) }}
			>
				{(subIndex === 0 || isNotDesktopView) && (
					<div className={styles.associationCellTitle}>
						{associationType === ASSOCIATION_TYPE.INTERNAL
							? dictionary.table?.internalAssociation ||
							"Internal Association"
							: dictionary.table?.externalAssociation ||
							"External Association"}
						{displayConditions.isRequired && asterisk}
					</div>
				)}
				<style>
					{/* Refer to CRUX-7360 */}
					{`.${styles.associationSearch} .ui.dropdown .menu>.disabled.item { display: none; }`}
				</style>
				<div className={styles.associationCellInner}>
					{guestMode ? <FlowableOrgSearch
						{...restProps}
						config={{
							...associationConfig,
							value: association.association,
							$errors: validateIsRequired(association.association, associationConfig?.isRequired)
						}}
						onChange={associationChanged}
						hideLabel={true}
						className="associationSearchWrapper"
					             /> :
						<FlowableFinder
							{...restProps}
							searchChange={associationChanged}
							preselectedOptions={preselectedOptions}
							config={{
								...associationConfig,
								value: association.association,
								$errors: validateIsRequired(association.association, associationConfig?.isRequired)
							}}
							hideLabel={true}
							className="associationSearchWrapper"
						/>}
				</div>
			</div>
			{associationType === ASSOCIATION_TYPE.INTERNAL &&
				<>
					{displayConditions.creditField &&
						<div className={styles.cellWrapper} style={{ width: getCellWidth("creditField", isSmallDesktopView, isTabletView) }}>

							{(subIndex === 0 || isNotDesktopView) && (
								<div className={styles.multiCellTitle}>
									{dictionary.table?.credit || "Credit"}
									{displayConditions.creditFieldRequired && asterisk}
								</div>
							)}
							<Components.number
								{...restProps}
								ckey={`${rowKey}-${subIndex}-credit`}
								key={`${rowKey}-${subIndex}-credit`}
								config={{
									...creditConfig,
									value: association.credit,
									$errors: validateIsRequired(association.credit, displayConditions.creditFieldRequired)
								}}
								onChange={creditChanged}
								className={`credit ${styles.fieldInput} ${getValidateRequiredClass(association.credit, creditConfig.enabled, displayConditions.creditFieldRequired)} rt-credit`}
							/>
						</div>
					}
					{displayConditions.sponsoredEffort &&
						<div className={styles.multiCellWrapper} style={{ width: getCellWidth("sponsoredEffort", isSmallDesktopView, isTabletView) }}>
							{(subIndex === 0 || isNotDesktopView) && (
								<div className={styles.multiCellTitle}>
									{dictionary.table?.sponsoredEffort || "Sponsored Effort"}
									{displayConditions.sponsoredEffortRequired && asterisk}
								</div>
							)}
							<div className={styles.multiCellInner}>
								<div className={styles.multiCellInnerItem}>
									{(subIndex === 0 || isNotDesktopView) && (
										<p
											style={{ fontSize: "10px" }}
											className={styles.horizCenter}
											title={dictionary.table?.calendar || "Calendar"}
										>
											{(dictionary.table?.calendar || "Calendar").charAt(0)}
										</p>
									)}
									<Components.number
										{...restProps}
										ckey={`${rowKey}-${subIndex}-sponsored-calendar`}
										key={`${rowKey}-${subIndex}-sponsored-calendar-${fieldFormat}`}
										config={{
											...sponsoredConfigs.calendar,
											value: association[`sponsoredEffort${fieldFormat}`]?.calendar,
											$errors: validateEffort(
												association[`sponsoredEffort${fieldFormat}`]?.calendar,
												sponsoredConfigs.calendar.extraSettings.max,
												(sponsoredConfigs.calendar.enabled && displayConditions.sponsoredEffortRequired)
											)
										}}
										onChange={value => setEffortValue("calendar", EFFORT_TYPES[0], sanitizeNumberValue(value))}
										className={`calendarSponsoredEffort ${styles.fieldInput} ${styles[effortFormat]} ${getValidateRequiredClass(association[`sponsoredEffort${fieldFormat}`]?.calendar, sponsoredConfigs.calendar.enabled, displayConditions.sponsoredEffortRequired)}`}
									/>
								</div>
								<div className={styles.multiCellInnerItem}>
									{(subIndex === 0 || isNotDesktopView) && (
										<p
											style={{ fontSize: "10px" }}
											className={styles.horizCenter}
											title={dictionary.table?.academic || "Academic"}
										>
											{(dictionary.table?.academic || "Academic").charAt(0)}
										</p>
									)}
									<Components.number
										{...restProps}
										ckey={`${rowKey}-${subIndex}-sponsored-academic`}
										key={`${rowKey}-${subIndex}-sponsored-academic-${fieldFormat}`}
										config={{
											...sponsoredConfigs.academic,
											value: association[`sponsoredEffort${fieldFormat}`]?.academic,
											$errors: validateEffort(
												association[`sponsoredEffort${fieldFormat}`]?.academic,
												sponsoredConfigs.academic.extraSettings.max,
												(sponsoredConfigs.academic.enabled && displayConditions.sponsoredEffortRequired)
											)
										}}
										onChange={value => setEffortValue("academic", EFFORT_TYPES[0], sanitizeNumberValue(value))}
										className={`academicSponsoredEffort ${styles.fieldInput} ${styles[effortFormat]} ${getValidateRequiredClass(association[`sponsoredEffort${fieldFormat}`]?.academic, sponsoredConfigs.academic.enabled, displayConditions.sponsoredEffortRequired)}`}
									/>
								</div>
								<div className={styles.multiCellInnerItem}>
									{(subIndex === 0 || isNotDesktopView) && (
										<p
											style={{ fontSize: "10px" }}
											className={styles.horizCenter}
											title={dictionary.table?.summer || "Summer"}
										>
											{(dictionary.table?.summer || "Summer").charAt(0)}
										</p>
									)}
									<Components.number
										{...restProps}
										ckey={`${rowKey}-${subIndex}-sponsored-summer`}
										key={`${rowKey}-${subIndex}-sponsored-summer-${fieldFormat}`}
										config={{
											...sponsoredConfigs.summer,
											value: association[`sponsoredEffort${fieldFormat}`]?.summer,
											$errors: validateEffort(
												association[`sponsoredEffort${fieldFormat}`]?.summer,
												sponsoredConfigs.summer.extraSettings.max,
												(sponsoredConfigs.summer.enabled && displayConditions.sponsoredEffortRequired)
											)
										}}
										onChange={value => setEffortValue("summer", EFFORT_TYPES[0], sanitizeNumberValue(value))}
										className={`summerSponsoredEffort ${styles.fieldInput} ${styles[effortFormat]} ${getValidateRequiredClass(association[`sponsoredEffort${fieldFormat}`]?.summer, sponsoredConfigs.summer.enabled, displayConditions.sponsoredEffortRequired)}`}
									/>
								</div>
							</div>
						</div>
					}
					{displayConditions.costShareEffort &&
						<div className={styles.multiCellWrapper} style={{ width: getCellWidth("costShareEffort", isSmallDesktopView, isTabletView) }}>
							{(subIndex === 0 || isNotDesktopView) && (
								<div className={styles.multiCellTitle}>
									{dictionary.table?.costShareEffort || "Cost Share Effort"}
									{displayConditions.costShareEffortRequired && asterisk}
								</div>
							)}
							<div className={styles.multiCellInner}>
								<div className={styles.multiCellInnerItem}>
									{(subIndex === 0 || isNotDesktopView) && (
										<p
											style={{ fontSize: "10px" }}
											className={styles.horizCenter}
											title={dictionary.table?.calendar || "Calendar"}
										>
											{(dictionary.table?.calendar || "Calendar").charAt(0)}
										</p>
									)}
									<Components.number
										{...restProps}
										ckey={`${rowKey}-${subIndex}-cost-share-calendar`}
										key={`${rowKey}-${subIndex}-cost-share-calendar-${fieldFormat}`}
										config={{
											...costShareEffortConfigs.calendar,
											value: association[`costShareEffort${fieldFormat}`]?.calendar,
											$errors: validateEffort(
												association[`costShareEffort${fieldFormat}`]?.calendar,
												costShareEffortConfigs.calendar.extraSettings.max,
												(costShareEffortConfigs.calendar.enabled && displayConditions.costShareEffortRequired)
											)
										}}
										onChange={value => setEffortValue("calendar", EFFORT_TYPES[1], sanitizeNumberValue(value))}
										className={`calendarCostShareEffort ${styles.fieldInput} ${styles[effortFormat]} ${getValidateRequiredClass(association[`costShareEffort${fieldFormat}`]?.calendar, costShareEffortConfigs.calendar.enabled, displayConditions.costShareEffortRequired)}`}
									/>
								</div>
								<div className={styles.multiCellInnerItem}>
									{(subIndex === 0 || isNotDesktopView) && (
										<p
											style={{ fontSize: "10px" }}
											className={styles.horizCenter}
											title={dictionary.table?.academic || "Academic"}
										>
											{(dictionary.table?.academic || "Academic").charAt(0)}
										</p>
									)}
									<Components.number
										{...restProps}
										ckey={`${rowKey}-${subIndex}-cost-share-academic`}
										key={`${rowKey}-${subIndex}-cost-share-academic-${fieldFormat}`}
										config={{
											...costShareEffortConfigs.academic,
											value: association[`costShareEffort${fieldFormat}`]?.academic,
											$errors: validateEffort(
												association[`costShareEffort${fieldFormat}`]?.academic,
												costShareEffortConfigs.academic.extraSettings.max,
												(costShareEffortConfigs.academic.enabled && displayConditions.costShareEffortRequired))
										}}
										onChange={value => setEffortValue("academic", EFFORT_TYPES[1], sanitizeNumberValue(value))}
										className={`academicCostShareEffort ${styles.fieldInput} ${styles[effortFormat]} ${getValidateRequiredClass(association[`costShareEffort${fieldFormat}`]?.academic, costShareEffortConfigs.academic.enabled, displayConditions.costShareEffortRequired)}`}
									/>
								</div>
								<div className={styles.multiCellInnerItem}>
									{(subIndex === 0 || isNotDesktopView) && (
										<p
											style={{ fontSize: "10px" }}
											className={styles.horizCenter}
											title={dictionary.table?.summer || "Summer"}
										>
											{(dictionary.table?.summer || "Summer").charAt(0)}
										</p>
									)}
									<Components.number
										{...restProps}
										ckey={`${rowKey}-${subIndex}-cost-share-summer`}
										key={`${rowKey}-${subIndex}-cost-share-summer-${fieldFormat}`}
										config={{
											...costShareEffortConfigs.summer,
											value: association[`costShareEffort${fieldFormat}`]?.summer,
											$errors: validateEffort(
												association[`costShareEffort${fieldFormat}`]?.summer,
												costShareEffortConfigs.summer.extraSettings.max,
												(costShareEffortConfigs.summer.enabled && displayConditions.costShareEffortRequired)
											)
										}}
										onChange={value => setEffortValue("summer",  EFFORT_TYPES[1], sanitizeNumberValue(value))}
										className={`summerCostShareEffort 
											${styles.fieldInput} 
											${styles[effortFormat]} 
											${getValidateRequiredClass(association[`costShareEffort${fieldFormat}`]?.summer, costShareEffortConfigs.summer.enabled, displayConditions.costShareEffortRequired)}`}
									/>
								</div>
							</div>
						</div>
					}
					{displayConditions.shouldDisplayTotalEffort &&
						<div className={styles.summarizeCellWrapper} style={{ width: getCellWidth("totalEffort", isSmallDesktopView, isTabletView) }}>
							{(subIndex === 0 || isNotDesktopView) && (
								<div title={dictionary.table?.totalEffort || "Total Effort"} className={styles.totalEffortCellInner}>
									{dictionary.table?.totalEffort ||
										"Total Effort"}
								</div>
							)}
							<div className={`${styles.centerAligned} ${styles.fieldInput} ${styles.totalEffort} ${styles[effortFormat]}`}>
								{association[`totalEffort${fieldFormat}`] && !isNaN(association[`totalEffort${fieldFormat}`]) ?
									association[`totalEffort${fieldFormat}`] : "- -"}
							</div>
						</div>
					}
				</>
			}
			<div className={`${styles.horizCenter} ${styles.removeAssociationRowButton}`}>
				{subIndex !== 0 && trashIconEnabled &&
					<CovertButton
						onClick={() => setShowModal(true)}
						aria-label={dictionary.removeAssociationButton || "Remove Association"}
					>
						<Icon
							className={styles.blue}
							name="trash"
							size="bigger"
						/>
					</CovertButton>
				}
			</div>
			{showModal &&
				<AlertDialog
					title={dictionary.removeAssociationTitle ?? "Remove association?"}
					description={
						association?.lookup?.text
							? <>Remove <b>{associationLookup.text}</b> association?</>
							: "Remove association?"
					}
					isOpen={showModal}
					onConfirm={remove}
					confirmText={dictionary.removeAssociationButton || "Remove Association"}
					onCancel={() => setShowModal(false)}
				/>
			}
		</div>
	);
};
